import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Chip, Stack } from '@mui/material';

import { formatMoney } from '../utils';
import type { DealOptionsInclusionsChecklistRow } from '../utils';

export const INCLUSION_COLUMNS = '160px 160px 110px 160px 160px 110px 130px 100px';

interface Props {
  currency: string;
  inc: DealOptionsInclusionsChecklistRow;
  manuallyApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function InclusionRow({ currency, inc, manuallyApproved, onApprovalChange, onUnapprovalChange }: Props) {
  const isApproved = inc.status === 'approved' || manuallyApproved;
  const dotColor = isApproved ? 'success.main' : 'warning.main';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: INCLUSION_COLUMNS,
        gap: 1.5,
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        px: 2,
        py: 1,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box sx={fieldBoxSx}>{inc.name || '—'}</Box>
      <Box sx={fieldBoxSx}>{inc.description || '—'}</Box>
      <Box sx={fieldBoxSx}>{inc.type || '—'}</Box>
      <Box sx={fieldBoxSx}>{formatMoney(inc.dealOptionInclusionRrp, currency)}</Box>
      <Box sx={fieldBoxSx}>{formatMoney(inc.inclusionAssetValue, currency)}</Box>
      <Box sx={fieldBoxSx}>{formatMoney(inc.totalValue, currency)}</Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          component="span"
          sx={{
            bgcolor: dotColor,
            borderRadius: '50%',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.04)',
            display: 'inline-block',
            flexShrink: 0,
            height: 10,
            width: 10,
          }}
        />
        {manuallyApproved && (
          <Chip label="Manual" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        )}
      </Stack>
      <Box>
        {manuallyApproved ? (
          <Button size="small" variant="outlined" color="error" onClick={() => onUnapprovalChange(inc.id)}>
            Unapprove
          </Button>
        ) : !isApproved && inc.status !== 'blocked' ? (
          <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={() => onApprovalChange(inc.id)}>
            Approve
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

const fieldBoxSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  fontSize: 13,
  overflow: 'hidden',
  px: 1.5,
  py: 0.75,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
